import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { makeApi, aiErrorText, coinToast } from "@/lib/api";
import { useT } from "@/i18n";

const oops = () => toast.error("Ups, no se pudo guardar. Intenta otra vez.");

export default function useAppProject() {
  const { lang } = useT();
  const { getToken, isSignedIn } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const timer = useRef(null);
  const pending = useRef({});

  const load = useCallback(() => {
    setLoadError(false);
    api.get("/apps/me").then((r) => setProject(r.data)).catch(() => setLoadError(true));
  }, [api]);

  useEffect(() => { if (isSignedIn) load(); }, [isSignedIn, load]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const body = pending.current;
    pending.current = {};
    if (!Object.keys(body).length) return;
    try {
      await api.put("/apps/me", body);
    } catch {
      oops();
      throw new Error("save failed");
    }
  }, [api]);

  useEffect(() => () => { flush().catch(() => {}); }, [flush]);

  const update = useCallback((fields) => {
    setProject((p) => ({ ...p, ...fields }));
    pending.current = { ...pending.current, ...fields };
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush().catch(() => {}), 700);
  }, [flush]);

  const complete = useCallback(async (id) => {
    try {
      await flush();
      const r = await api.post(`/apps/me/missions/${id}`);
      setProject((p) => ({ ...p, missions: r.data.project.missions }));
      return r.data.awarded;
    } catch {
      oops();
      return null;
    }
  }, [api, flush]);

  const share = useCallback(async () => {
    try {
      await flush();
      const r = await api.post("/apps/me/share");
      setProject((p) => ({ ...p, shared: r.data.shared, share_id: r.data.share_id }));
      coinToast(r.data.coins_awarded, lang);
    } catch (e) {
      if (e?.response?.status === 422 || e?.response?.status === 503) toast.error(aiErrorText(e, lang, ""));
      else oops();
    }
  }, [api, flush, lang]);

  return { project, update, complete, share, isSignedIn, api, loadError, reload: load };
}
